import type {
  NavChild,
  NavEntry,
  NavGroup,
  NavSection,
} from '@/components/shared/Sidebar/types';
import type { useFeatures } from '@/hooks/useFeatures';

export function buildNav(
  f: ReturnType<typeof useFeatures>,
  t: (key: string) => string,
  canAccessEntityScreening: boolean,
): NavEntry[] {
  const traceabilityChildren: NavChild[] = [
    {
      key: 'chain-mapping',
      label: t('supplyChainMapping'),
      href: '/supply-chain-mapping',
      visible: () => f.isFeatureEnabled('supply-chain-mapping'),
    },
    {
      key: 'product-mapping',
      label: t('productMapping'),
      href: '/product-mapping',
      visible: () => f.isProductMappingEnabled,
    },
    {
      key: 'orders',
      label: t('orders'),
      href: '/orders',
      visible: () => f.isCascadeTracingEnabled,
    },
    {
      key: 'orders-hub',
      label: t('ordersHub'),
      href: '/orders-hub',
      visible: () => f.isOrdersHubEnabled && f.isCascadeTracingEnabled,
    },
    {
      key: 'entity-screening-v2',
      label: t('entityScreeningV2'),
      href: '/entity-screening',
      visible: () => f.isEntityScreeningV2Enabled && canAccessEntityScreening,
    },
    {
      key: 'certified-materials',
      label: t('certifiedMaterials'),
      href: '/certified-materials',
      visible: () =>
        f.isFeatureEnabled('certified-materials-brand') ||
        f.isFeatureEnabled('certified-materials-supplier'),
    },
    {
      key: 'tcm',
      label: t('tcManagement'),
      href: '/traceability/transaction-certificates',
      visible: () => f.isFeatureEnabled('transaction-certificate-management'),
    },
    {
      key: 'fiber-transactions',
      label: t('transactions'),
      href: '/transactions',
      visible: () => f.isFeatureEnabled('fiber-forward'),
    },
    {
      key: 'fiber-productions',
      label: t('productions'),
      href: '/productions',
      visible: () => f.isFeatureEnabled('fiber-forward'),
    },
    {
      key: 'fiber-inventory',
      label: t('inventory'),
      href: '/inventory/products',
      matchPrefix: '/inventory',
      visible: () => f.isFeatureEnabled('fiber-forward'),
    },
    {
      key: 'files-management',
      label: t('filesManagement'),
      href: '/files',
      visible: () =>
        f.isCascadeTracingEnabled &&
        f.isFeatureEnabled('tracing-document-management'),
    },
  ];

  const reportChildren: NavChild[] = [
    {
      key: 'rpt-tracing-ops',
      label: t('tracingOperations'),
      href: '/report/tracing-operations',
      visible: () => f.isCascadeTracingEnabled,
    },
    {
      key: 'rpt-tracing-comp',
      label: t('tracingCompliance'),
      href: '/report/tracing-compliance',
      visible: () => f.isCascadeTracingEnabled,
    },
    {
      key: 'rpt-cert-comp',
      label: t('certificateCompliance'),
      href: '/report/certificate-compliance',
    },
    {
      key: 'rpt-audit-comp',
      label: t('auditCompliance'),
      href: '/report/audit-compliance',
    },
  ];

  return [
    {
      type: 'item',
      key: 'dashboard',
      label: t('dashboard'),
      icon: 'fa-light fa-house',
      href: '/dashboard',
    },
    {
      type: 'item',
      key: 'network',
      label: t('network'),
      icon: 'fa-light fa-building',
      href: '/network',
    },
    { type: 'section', label: t('sectionCatalog') },
    {
      type: 'group',
      key: 'products',
      label: t('products'),
      icon: 'fa-light fa-clothes-hanger',
      children: [
        { key: 'my-styles', label: t('myStyles'), href: '/my-styles' },
        {
          key: 'my-components',
          label: t('myComponents'),
          href: '/my-components',
        },
      ],
    },
    {
      type: 'group',
      key: 'compliance',
      label: t('vendorCompliance'),
      shortLabel: t('vendorComplianceShort'),
      icon: 'fa-light fa-ballot-check',
      children: [
        {
          key: 'questionnaires',
          label: t('questionnaires'),
          href: '/questionnaires',
        },
        {
          key: 'documents',
          label: t('documents'),
          href: '/documents',
        },
        {
          key: 'certifications',
          label: t('certifications'),
          href: '/certifications',
        },
        {
          key: 'corrective-actions',
          label: t('capa'),
          href: '/capa',
          visible: () =>
            f.isCapaEnabled ||
            f.isCapaV2Enabled ||
            f.isCapaBasicEnabled ||
            f.isCapaAdvancedEnabled,
        },
        { key: 'audits', label: t('audits'), href: '/audits' },
        {
          key: 'slcps',
          label: t('slcps'),
          href: '/slcp/my-own/active',
          matchPrefix: '/slcp',
        },
      ],
    },
    {
      type: 'group',
      key: 'traceability',
      label: t('traceability'),
      shortLabel: t('traceabilityShort'),
      icon: 'fa-light fa-route',
      children: traceabilityChildren,
    },
    { type: 'section', label: t('sectionRisk') },
    {
      type: 'group',
      key: 'risk',
      label: t('riskManagement'),
      shortLabel: t('riskManagementShort'),
      icon: 'fa-light fa-mountains',
      children: [
        {
          key: 'risk-landscape',
          label: t('riskLandscape'),
          href: '/risk/landscape',
        },
        { key: 'rules', label: t('rules'), href: '/risk/rules' },
        {
          key: 'assessments',
          label: t('assessments'),
          href: '/risk/assessments',
        },
      ],
    },
    ...(f.isFeatureEnabled('self-service-api-key') ||
    f.isFeatureEnabled('constants-mapping')
      ? [
          { type: 'section', label: t('sectionDeveloper') } as NavSection,
          {
            type: 'group',
            key: 'developer',
            label: t('developersHQ'),
            shortLabel: t('developersHQShort'),
            icon: 'fa-light fa-square-code',
            children: [
              {
                key: 'api-keys',
                label: t('apiKeys'),
                href: '/api-keys',
                visible: () => f.isFeatureEnabled('self-service-api-key'),
              },
              {
                key: 'constants-mapping',
                label: t('constantsMapping'),
                href: '/constants-mapping/companies',
                matchPrefix: '/constants-mapping',
                visible: () => f.isFeatureEnabled('constants-mapping'),
              },
            ],
          } as NavGroup,
        ]
      : []),
    { type: 'section', label: t('sectionReports') },
    {
      type: 'group',
      key: 'reports',
      label: t('reports'),
      icon: 'fa-light fa-chart-line',
      children: reportChildren,
    },
    { type: 'section', label: t('sectionResources') },
    {
      type: 'item',
      key: 'help-center',
      label: t('helpCenter'),
      shortLabel: t('helpCenterShort'),
      icon: 'fa-light fa-book',
      onClick: () => window.open('https://help.retraced.com/', '_blank'),
    },
    {
      type: 'item',
      key: 'hrdd-academy',
      label: t('hrddAcademy'),
      shortLabel: 'HRDD',
      icon: 'fa-light fa-graduation-cap',
      onClick: () => window.open('https://www.hrddacademy.com/', '_blank'),
      accent: true,
    },
  ];
}
